import Phaser from 'phaser';
import type { NodeDef } from '../sim/types';
import { COLORS, nodeStyle } from './palette';
import { drawNodeMark } from './nodeMark';

const RADIUS = 51;
const HIT_RADIUS = 84; // generous finger-friendly tap zone (1080×1920)

export class NodeView {
  readonly def: NodeDef;
  readonly container: Phaser.GameObjects.Container;
  private hit!: Phaser.GameObjects.Zone;
  private aura: Phaser.GameObjects.Arc;
  private auraInner: Phaser.GameObjects.Arc;
  private bg: Phaser.GameObjects.Arc;
  private ring: Phaser.GameObjects.Arc;
  private ringSoft: Phaser.GameObjects.Arc;
  private tickRim: Phaser.GameObjects.Graphics;
  private innerGlow: Phaser.GameObjects.Arc;
  private innerMark: Phaser.GameObjects.Graphics;
  private chargePips: Phaser.GameObjects.Graphics;
  private shieldRing?: Phaser.GameObjects.Graphics;
  private shieldRotor = 0;
  private typeFx?: Phaser.GameObjects.Graphics;
  private typeFxT = 0;
  private label?: Phaser.GameObjects.Text;
  private subLabel?: Phaser.GameObjects.Text;
  private style = nodeStyle('normal');
  private collapsed = false;
  private shielded = false;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, def: NodeDef, isShielded: boolean) {
    this.scene = scene;
    this.def = def;
    this.style = nodeStyle(def.type);

    this.container = scene.add.container(def.x, def.y);

    // soft outer aura (additive bloom)
    this.aura = scene.add
      .circle(0, 0, RADIUS + 22, this.style.ring, 0.07)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.auraInner = scene.add
      .circle(0, 0, RADIUS + 10, this.style.ring, 0.14)
      .setBlendMode(Phaser.BlendModes.ADD);

    // base disc
    this.bg = scene.add.circle(0, 0, RADIUS, this.style.fill);

    // Inner texture: two faint concentric rings — gives the disc a sense of
    // mechanical depth rather than reading as a flat shape.
    const texture = scene.add.graphics();
    texture.lineStyle(1, this.style.ring, 0.18);
    texture.strokeCircle(0, 0, RADIUS * 0.72);
    texture.lineStyle(1, this.style.ring, 0.10);
    texture.strokeCircle(0, 0, RADIUS * 0.45);

    // soft outer ring (wider, fainter) for halo edge
    this.ringSoft = scene.add
      .circle(0, 0, RADIUS + 2, undefined, 0)
      .setStrokeStyle(6, this.style.ring, 0.22);

    // main ring
    this.ring = scene.add
      .circle(0, 0, RADIUS, undefined, 0)
      .setStrokeStyle(2.5, this.style.ring, 1);

    // rotating notched rim (gives the sci-fi rotor feel)
    this.tickRim = scene.add.graphics();
    this.drawTickRim();

    // small inner accent glow
    this.innerGlow = scene.add
      .circle(0, 0, RADIUS * 0.55, this.style.ring, 0.10)
      .setBlendMode(Phaser.BlendModes.ADD);

    this.innerMark = scene.add.graphics();
    this.chargePips = scene.add.graphics();
    this.drawInnerMark();

    // type-specific animated FX overlay (capacitor arc, core sparkle, etc.)
    if (def.type === 'capacitor' || def.type === 'core' || def.type === 'splitter') {
      this.typeFx = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    }

    this.container.add([
      this.aura,
      this.auraInner,
      this.bg,
      texture,
      this.ringSoft,
      this.ring,
      this.tickRim,
      this.innerGlow,
      this.innerMark,
      this.chargePips,
    ]);
    if (this.typeFx) this.container.add(this.typeFx);

    if (isShielded) {
      this.shielded = true;
      this.shieldRing = scene.add.graphics();
      this.drawShieldRing();
      this.container.add(this.shieldRing);
    }

    if (def.label) {
      this.label = scene.add
        .text(0, RADIUS + 20, def.label, {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '32px',
          color: '#cdd8f5',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0);
      this.label.setShadow(0, 0, '#000000', 6, true, true);
      this.container.add(this.label);

      const tag = this.typeTag();
      if (tag) {
        const tagColor = this.tagColorHex();
        this.subLabel = scene.add
          .text(0, RADIUS + 66, tag, {
            fontFamily: 'ui-monospace, monospace',
            fontSize: '28px',
            color: tagColor,
            fontStyle: 'bold',
          })
          .setOrigin(0.5, 0);
        this.subLabel.setShadow(0, 0, tagColor, 12, true, true);
        this.container.add(this.subLabel);
      }
    }

    // Dedicated hit zone at world coords, ABOVE the node visuals.
    // Single setInteractive call with a Circle centered at the zone's
    // local center (HIT_RADIUS, HIT_RADIUS) — known-good Phaser pattern.
    this.hit = scene.add
      .zone(def.x, def.y, HIT_RADIUS * 2, HIT_RADIUS * 2)
      .setInteractive(
        new Phaser.Geom.Circle(HIT_RADIUS, HIT_RADIUS, HIT_RADIUS),
        Phaser.Geom.Circle.Contains,
      );
    if (this.hit.input) this.hit.input.cursor = 'pointer';

    // hover affordance: brighten the rim
    this.hit.on('pointerover', () => {
      if (this.collapsed || this.def.conduit) return;
      this.ringSoft.setStrokeStyle(8, this.style.ringActive, 0.45);
      this.auraInner.setAlpha(0.28);
    });
    this.hit.on('pointerout', () => {
      if (this.collapsed) return;
      this.ringSoft.setStrokeStyle(6, this.style.ring, 0.22);
      this.auraInner.setAlpha(0.14);
    });

    // A conduit carries the pulse but is not a tappable starter: dim it so it
    // never reads as an invitation to tap.
    if (def.conduit) {
      this.ring.setAlpha(0.5);
      this.ringSoft.setAlpha(0.4);
      this.innerGlow.setAlpha(0.5);
      this.tickRim.setAlpha(0.5);
      if (this.hit.input) this.hit.input.cursor = 'default';
    }

    // idle subtle pulse for required + core to draw the eye
    if (def.type === 'required' || def.type === 'core') {
      scene.tweens.add({
        targets: [this.ring, this.auraInner],
        scale: { from: 1, to: 1.08 },
        alpha: { from: 1, to: 0.55 },
        yoyo: true,
        repeat: -1,
        duration: 1100,
        ease: 'Sine.easeInOut',
      });
    } else if (def.type === 'protected') {
      // Faster, harsher aura pulse — eye flinches away from it.
      scene.tweens.add({
        targets: this.aura,
        alpha: { from: 0.15, to: 0.40 },
        scale: { from: 1, to: 1.18 },
        yoyo: true,
        repeat: -1,
        duration: 720,
        ease: 'Sine.easeInOut',
      });
    } else {
      // gentler breathe for everyone else so the field feels alive
      scene.tweens.add({
        targets: this.aura,
        alpha: { from: 0.07, to: 0.16 },
        yoyo: true,
        repeat: -1,
        duration: 1600 + Math.random() * 400,
        ease: 'Sine.easeInOut',
      });
    }

    // Rotate the tick rim slowly. Throttle so we only redraw when the
    // rotation has changed by >0.05 rad — saves ~80% of clear+stroke work.
    let lastRimRot = -999;
    scene.tweens.addCounter({
      from: 0,
      to: Math.PI * 2,
      duration: 9000 + Math.random() * 2000,
      repeat: -1,
      onUpdate: (tw) => {
        if (this.collapsed) return;
        const v = tw.getValue() ?? 0;
        if (Math.abs(v - lastRimRot) < 0.05) return;
        lastRimRot = v;
        this.drawTickRim(v);
      },
    });

    // Shield ring rotation — throttled like the tick rim above.
    if (this.shieldRing) {
      let lastShieldRot = -999;
      scene.tweens.addCounter({
        from: 0,
        to: Math.PI * 2,
        duration: 5000,
        repeat: -1,
        onUpdate: (tw) => {
          const v = tw.getValue() ?? 0;
          if (Math.abs(v - lastShieldRot) < 0.04) return;
          lastShieldRot = v;
          this.shieldRotor = v;
          if (this.shieldRing) this.drawShieldRing();
        },
      });
    }

    // Type-specific FX driver. Throttled to ~20fps (every 0.05 of progress)
    // since fine detail at 60fps isn't perceivable for these blink effects.
    if (this.typeFx) {
      let lastFxT = -1;
      scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: def.type === 'core' ? 1800 : 1200,
        repeat: -1,
        onUpdate: (tw) => {
          const v = tw.getValue() ?? 0;
          if (Math.abs(v - lastFxT) < 0.04) return;
          lastFxT = v;
          this.typeFxT = v;
          if (this.collapsed) return;
          this.drawTypeFx();
        },
      });
    }
  }

  /**
   * Per-type animated overlay drawn ON TOP of the inner mark. Only spawned
   * for types that benefit from it (capacitor, core, splitter).
   */
  private drawTypeFx() {
    const g = this.typeFx!;
    g.clear();
    const t = this.typeFxT;
    switch (this.def.type) {
      case 'capacitor': {
        // arcing bolt between the two plates of the capacitor symbol
        const a = (Math.sin(t * Math.PI * 2) + 1) / 2; // 0..1
        const segments = 5;
        const startX = -4;
        const endX = 4;
        g.lineStyle(1.5, 0xffff99, 0.4 + a * 0.55);
        g.beginPath();
        g.moveTo(startX, 0);
        for (let i = 1; i <= segments; i++) {
          const px = startX + ((endX - startX) * i) / segments;
          const py = (Math.random() - 0.5) * 5 * a;
          g.lineTo(px, py);
        }
        g.strokePath();
        // electrode glow nubs
        g.fillStyle(0xffffaa, 0.5 + a * 0.4);
        g.fillCircle(-4, 0, 1.2 + a * 1.4);
        g.fillCircle(4, 0, 1.2 + a * 1.4);
        break;
      }
      case 'core': {
        // rotating radial sparks emanating from the reactor
        const sparks = 8;
        for (let i = 0; i < sparks; i++) {
          const phase = (i / sparks + t) % 1;
          const r0 = 14 + phase * 12;
          const a0 = (i / sparks) * Math.PI * 2 + t * Math.PI * 2;
          const x = Math.cos(a0) * r0;
          const y = Math.sin(a0) * r0;
          g.fillStyle(0xeeaaff, (1 - phase) * 0.85);
          g.fillCircle(x, y, 1.6);
        }
        // pulsing core dot
        const pulse = (Math.sin(t * Math.PI * 2) + 1) / 2;
        g.fillStyle(0xffffff, 0.4 + pulse * 0.55);
        g.fillCircle(0, 0, 2.5 + pulse * 1.8);
        break;
      }
      case 'splitter': {
        // arrowtips on the three Y branches blink in rotation
        const branches = [
          { x: 0, y: -10 },
          { x: -9, y: 7 },
          { x: 9, y: 7 },
        ];
        for (let i = 0; i < branches.length; i++) {
          const phase = (i / 3 + t) % 1;
          const flash = phase < 0.25 ? (1 - phase * 4) : 0;
          if (flash > 0) {
            g.fillStyle(0xffaae0, flash * 0.9);
            g.fillCircle(branches[i].x, branches[i].y, 3 + flash * 2);
          }
        }
        break;
      }
    }
  }

  private typeTag(): string {
    switch (this.def.type) {
      case 'required':  return 'REQUIRED';
      case 'protected': return 'PROTECTED';
      case 'relay':     return 'RELAY';
      case 'shield':    return 'SHIELD';
      case 'splitter':  return 'SPLITTER';
      case 'capacitor': return 'CAPACITOR';
      case 'core':      return 'CORE';
      default:          return this.def.conduit ? 'CONDUIT' : '';
    }
  }

  private tagColorHex(): string {
    switch (this.def.type) {
      case 'required':  return '#6cf0ff';
      case 'protected': return '#ff7a99';
      case 'relay':     return '#ffd070';
      case 'shield':    return '#88ffb0';
      case 'splitter':  return '#ff99dd';
      case 'capacitor': return '#fff088';
      case 'core':      return '#dd99ff';
      default:          return '#7a86a8';
    }
  }

  private drawTickRim(rotation = 0) {
    const g = this.tickRim;
    g.clear();
    const r = RADIUS + 7;
    const ticks = 16;
    g.lineStyle(2, this.style.ring, 0.55);
    for (let i = 0; i < ticks; i++) {
      const a = rotation + (i / ticks) * Math.PI * 2;
      const isLong = i % 4 === 0;
      const inner = r - (isLong ? 5 : 2.5);
      const outer = r + (isLong ? 1 : 0);
      g.lineBetween(
        Math.cos(a) * inner, Math.sin(a) * inner,
        Math.cos(a) * outer, Math.sin(a) * outer,
      );
    }
  }

  private drawShieldRing() {
    const g = this.shieldRing!;
    g.clear();
    const rOuter = RADIUS + 12;
    const rInner = RADIUS + 6;

    // Outer ring: marching dashes (rotates one way)
    const dashes = 18;
    g.lineStyle(2, 0x88ffbb, 0.85);
    for (let i = 0; i < dashes; i++) {
      const a0 = this.shieldRotor + (i / dashes) * Math.PI * 2;
      const a1 = a0 + (Math.PI * 2) / dashes * 0.55;
      g.beginPath();
      g.arc(0, 0, rOuter, a0, a1, false);
      g.strokePath();
    }

    // Inner ring: shorter dashes counter-rotating, gives the weave illusion
    const inDashes = 24;
    g.lineStyle(1.5, 0x44ff88, 0.55);
    for (let i = 0; i < inDashes; i++) {
      const a0 = -this.shieldRotor * 1.7 + (i / inDashes) * Math.PI * 2;
      const a1 = a0 + (Math.PI * 2) / inDashes * 0.35;
      g.beginPath();
      g.arc(0, 0, rInner, a0, a1, false);
      g.strokePath();
    }

    // Six hex anchor dots at outer ring, slow drift
    const anchors = 6;
    g.fillStyle(0xb0ffcc, 0.9);
    for (let i = 0; i < anchors; i++) {
      const a = this.shieldRotor * 0.6 + (i / anchors) * Math.PI * 2;
      const x = Math.cos(a) * rOuter;
      const y = Math.sin(a) * rOuter;
      g.fillCircle(x, y, 1.8);
    }
  }

  private drawInnerMark() {
    const g = this.innerMark;
    g.clear();
    drawNodeMark(g, this.def.type, RADIUS);
  }

  onPointerDown(handler: () => void) {
    this.hit.on('pointerdown', handler);
  }

  onHover(onEnter: () => void, onLeave: () => void) {
    this.hit.on('pointerover', onEnter);
    this.hit.on('pointerout', onLeave);
  }

  setInteractiveEnabled(enabled: boolean) {
    if (!this.hit.input) return;
    this.hit.input.enabled = enabled;
  }

  showCharge(current: number, threshold: number) {
    const g = this.chargePips;
    g.clear();
    if (threshold <= 1 || this.collapsed) return;
    const r = RADIUS - 8;
    const total = threshold;
    const arcSpan = Math.PI * 1.4;
    const start = -Math.PI / 2 - arcSpan / 2;
    for (let i = 0; i < total; i++) {
      const a = start + (arcSpan * i) / (total - 1 || 1);
      const px = Math.cos(a) * r;
      const py = Math.sin(a) * r;
      const filled = i < current;
      g.fillStyle(filled ? this.style.ringActive : this.style.ring, filled ? 1 : 0.3);
      g.fillCircle(px, py, filled ? 3.5 : 2.5);
      if (filled) {
        g.fillStyle(0xffffff, 0.6);
        g.fillCircle(px, py, 1.5);
      }
    }
  }

  flashCharge(scene: Phaser.Scene) {
    scene.tweens.killTweensOf(this.bg);
    this.bg.setScale(1);
    scene.tweens.add({
      targets: this.bg,
      scale: { from: 1.22, to: 1 },
      duration: 240,
      ease: 'Cubic.easeOut',
    });
    scene.tweens.add({
      targets: this.innerGlow,
      alpha: { from: 0.55, to: 0.10 },
      scale: { from: 1.5, to: 1 },
      duration: 280,
      ease: 'Cubic.easeOut',
    });
    scene.tweens.add({
      targets: this.ring,
      alpha: { from: 1, to: 0.9 },
      duration: 240,
    });

    // shockwave ring radiating from this node — subtle, but adds heft
    const wave = scene.add
      .circle(this.container.x, this.container.y, RADIUS, this.style.ringActive, 0)
      .setStrokeStyle(2, this.style.ringActive, 1)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: wave,
      radius: RADIUS * 1.8,
      alpha: { from: 0.7, to: 0 },
      duration: 360,
      ease: 'Cubic.easeOut',
      onComplete: () => wave.destroy(),
    });
  }

  flashDecay(scene: Phaser.Scene) {
    scene.tweens.add({
      targets: this.chargePips,
      alpha: { from: 1, to: 0.2, yoyo: true },
      duration: 180,
    });
  }

  setCollapsed(scene: Phaser.Scene) {
    if (this.collapsed) return;
    this.collapsed = true;
    scene.tweens.killTweensOf(this.ring);
    scene.tweens.killTweensOf(this.auraInner);

    const isCore = this.def.type === 'core';
    const cx = this.container.x;
    const cy = this.container.y;
    const ringCount = isCore ? 5 : 3;
    const sparkCount = isCore ? 18 : 8;
    const sizeScale = isCore ? 1.5 : 1.0;

    // ring shockwaves — more layers + bigger for core
    for (let i = 0; i < ringCount; i++) {
      const ring = scene.add
        .circle(cx, cy, RADIUS, this.style.ringActive, 0)
        .setStrokeStyle(3, this.style.ringActive, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: ring,
        radius: RADIUS * (3.0 + i * 0.7) * sizeScale,
        alpha: { from: 0.9 - i * 0.15, to: 0 },
        duration: (420 + i * 120) * (isCore ? 1.4 : 1),
        ease: 'Cubic.easeOut',
        delay: i * 60,
        onComplete: () => ring.destroy(),
      });
    }

    // bright core flash
    const burst = scene.add
      .circle(cx, cy, RADIUS, COLORS.collapseBurst, 0.95)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: burst,
      radius: RADIUS * 2.2 * sizeScale,
      alpha: 0,
      duration: 280 * (isCore ? 1.6 : 1),
      ease: 'Cubic.easeOut',
      onComplete: () => burst.destroy(),
    });

    // shrapnel sparks
    for (let i = 0; i < sparkCount; i++) {
      const a = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.4;
      const dist = (60 + Math.random() * 50) * sizeScale;
      const spark = scene.add
        .circle(cx, cy, 2.5, this.style.ringActive, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: spark,
        x: cx + Math.cos(a) * dist,
        y: cy + Math.sin(a) * dist,
        alpha: 0,
        duration: 480 * (isCore ? 1.4 : 1),
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    // CORE EXCLUSIVE — 8 thin energy spires radiating outward. Throttled.
    if (isCore) {
      const spireG = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
      let t = 0;
      const draw = () => {
        spireG.clear();
        const spires = 8;
        for (let i = 0; i < spires; i++) {
          const a = (i / spires) * Math.PI * 2 + t * Math.PI;
          const len = 105 + t * 330;
          const px = cx + Math.cos(a) * len;
          const py = cy + Math.sin(a) * len;
          spireG.lineStyle(3, 0xeeaaff, (1 - t) * 0.9);
          spireG.lineBetween(cx, cy, px, py);
          spireG.fillStyle(0xffffff, (1 - t) * 0.95);
          spireG.fillCircle(px, py, 4.5 * (1 - t * 0.5));
        }
      };
      draw();
      let lastT = -1;
      scene.tweens.addCounter({
        from: 0, to: 1,
        duration: 880,
        onUpdate: (tw) => {
          const v = tw.getValue() ?? 0;
          if (Math.abs(v - lastT) < 0.04) return;
          lastT = v;
          t = v;
          draw();
        },
        onComplete: () => spireG.destroy(),
        ease: 'Cubic.easeOut',
      });

      // a giant secondary aura that hangs around longer
      const aura = scene.add
        .circle(cx, cy, RADIUS * 1.4, 0xdd99ff, 0.7)
        .setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: aura,
        radius: RADIUS * 4.5,
        alpha: 0,
        duration: 1100,
        ease: 'Cubic.easeOut',
        onComplete: () => aura.destroy(),
      });
    }

    // node fades to a dim corpse
    scene.tweens.add({ targets: [this.ring, this.ringSoft], alpha: 0.25, duration: 320 });
    scene.tweens.add({ targets: [this.bg], alpha: 0.35, duration: 320 });
    scene.tweens.add({ targets: [this.innerMark, this.chargePips, this.tickRim, this.aura, this.auraInner, this.innerGlow], alpha: 0, duration: 260 });
    if (this.shieldRing) {
      scene.tweens.add({ targets: this.shieldRing, alpha: 0, duration: 220 });
    }
    if (this.label) this.label.setColor('#3a4060');
    if (this.subLabel) this.subLabel.setColor('#2a304a');
  }

  dropShield(scene: Phaser.Scene) {
    if (!this.shieldRing) return;
    this.shielded = false;
    const r = this.shieldRing;
    scene.tweens.add({
      targets: r,
      scale: { from: 1, to: 1.6 },
      alpha: { from: 1, to: 0 },
      duration: 360,
      ease: 'Cubic.easeOut',
      onComplete: () => r.destroy(),
    });
    this.shieldRing = undefined;
  }

  flashProtectedOverload(scene: Phaser.Scene) {
    const cx = this.container.x;
    const cy = this.container.y;

    // 1) Two layered shockwaves expanding outward, red
    for (let i = 0; i < 2; i++) {
      const ring = scene.add
        .circle(cx, cy, RADIUS, 0xff2244, 0)
        .setStrokeStyle(3, 0xff5577, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: ring,
        radius: RADIUS * (3.5 + i * 0.9),
        alpha: { from: 0.95 - i * 0.2, to: 0 },
        duration: 520 + i * 140,
        delay: i * 70,
        ease: 'Cubic.easeOut',
        onComplete: () => ring.destroy(),
      });
    }

    // 2) Central red burst
    const burst = scene.add
      .circle(cx, cy, RADIUS * 1.4, 0xff1133, 0.95)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: burst,
      radius: RADIUS * 3.2,
      alpha: 0,
      duration: 360,
      ease: 'Cubic.easeOut',
      onComplete: () => burst.destroy(),
    });

    // 3) Red shrapnel — 8 sparks scattering
    const sparks = 8;
    for (let i = 0; i < sparks; i++) {
      const a = (i / sparks) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 70 + Math.random() * 60;
      const spark = scene.add
        .circle(cx, cy, 2.5, 0xff5577, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      scene.tweens.add({
        targets: spark,
        x: cx + Math.cos(a) * dist,
        y: cy + Math.sin(a) * dist,
        alpha: 0,
        duration: 600 + Math.random() * 200,
        ease: 'Quad.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    scene.cameras.main.shake(300, 0.018);
  }

  /**
   * Quick cinematic target reticle drawn around the tap point, then expands
   * and fades as the cascade begins. Fires from GraphView on the `tap` event.
   */
  playTapReticle(scene: Phaser.Scene) {
    const reticle = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    let t = 0;
    const draw = (progress: number) => {
      reticle.clear();
      const r = RADIUS + 8 + progress * 26;
      const a = 1 - progress;
      reticle.lineStyle(2, 0x88e8ff, a * 0.95);
      // four arcs forming a [ ] reticle
      const span = Math.PI * 0.35;
      const offsets = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];
      for (const off of offsets) {
        reticle.beginPath();
        reticle.arc(this.container.x, this.container.y, r, off - span / 2, off + span / 2, false);
        reticle.strokePath();
      }
      // crosshair ticks at cardinal points
      const cx = this.container.x;
      const cy = this.container.y;
      reticle.lineStyle(1.5, 0x88e8ff, a * 0.85);
      reticle.lineBetween(cx, cy - r - 4, cx, cy - r - 12);
      reticle.lineBetween(cx, cy + r + 4, cx, cy + r + 12);
      reticle.lineBetween(cx - r - 4, cy, cx - r - 12, cy);
      reticle.lineBetween(cx + r + 4, cy, cx + r + 12, cy);
    };
    draw(0);
    scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 520,
      ease: 'Cubic.easeOut',
      onUpdate: (tw) => { t = tw.getValue() ?? 0; draw(t); },
      onComplete: () => reticle.destroy(),
    });
  }

  /**
   * Feedback for tapping a node that is NOT a valid breach point. A short red
   * ring flash + horizontal shake reads as "this isn't an entry point" without
   * a modal or text.
   */
  denyBreach(scene: Phaser.Scene) {
    const x0 = this.container.x;
    scene.tweens.killTweensOf(this.container);
    scene.tweens.add({
      targets: this.container,
      x: { from: x0 - 5, to: x0 + 5 },
      yoyo: true,
      repeat: 3,
      duration: 45,
      ease: 'Sine.easeInOut',
      onComplete: () => { this.container.x = x0; },
    });
    const warn = scene.add.circle(this.container.x, this.container.y, RADIUS + 6, undefined, 0)
      .setStrokeStyle(3, 0xff5577, 0.9)
      .setDepth(this.container.depth + 1);
    scene.tweens.add({
      targets: warn,
      scale: { from: 1, to: 1.4 },
      alpha: { from: 0.9, to: 0 },
      duration: 320,
      ease: 'Cubic.easeOut',
      onComplete: () => warn.destroy(),
    });
  }

  private eliminated = false;
  isEliminated(): boolean { return this.eliminated; }

  /**
   * Rule this breach point out (the hint reward). Draws a red X, dims the node,
   * and disables its hit zone so it can no longer be tapped.
   */
  markEliminated(scene: Phaser.Scene) {
    if (this.eliminated) return;
    this.eliminated = true;
    if (this.hit.input) this.hit.input.enabled = false;
    this.container.setAlpha(0.38);
    const x = scene.add.graphics();
    const r = RADIUS * 0.78;
    x.lineStyle(7, 0xff5577, 0.95);
    x.lineBetween(-r, -r, r, r);
    x.lineBetween(-r, r, r, -r);
    this.container.add(x);
    scene.tweens.add({
      targets: x, alpha: { from: 0, to: 1 }, scale: { from: 1.6, to: 1 },
      duration: 260, ease: 'Back.easeOut',
    });
  }

  setBreachHighlight(scene: Phaser.Scene, on: boolean) {
    if (on) {
      scene.tweens.add({
        targets: this.ring,
        scale: { from: 1, to: 1.15 },
        yoyo: true,
        repeat: -1,
        duration: 600,
      });
    } else {
      scene.tweens.killTweensOf(this.ring);
      this.ring.setScale(1);
    }
  }

  position(): { x: number; y: number } {
    return { x: this.container.x, y: this.container.y };
  }

  destroy() {
    this.hit?.destroy();
    this.container.destroy();
  }
}
